import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../config';

const ParentSchoolPolicies = ({ onBack }) => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPolicies();
  }, []);

  const loadPolicies = async () => {
    try {
      setLoading(true);
      const response = await axios.get(buildApiUrl('/api/school-policies'));
      if (response.data.success) {
        setPolicies(response.data.policies || []);
      }
    } catch (error) {
      console.error('Error loading policies:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (fileName) => {
    if (fileName.endsWith('.pdf')) {
      return (
        <svg className="w-10 h-10" viewBox="0 0 48 48" fill="none">
          <rect width="48" height="48" rx="4" fill="#E53E3E"/>
          <path d="M14 18h20M14 24h20M14 30h12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          <text x="24" y="38" fill="white" fontSize="10" fontWeight="bold" textAnchor="middle">PDF</text>
        </svg>
      );
    }
    if (fileName.endsWith('.doc') || fileName.endsWith('.docx')) {
      return (
        <svg className="w-10 h-10" viewBox="0 0 48 48" fill="none">
          <rect width="48" height="48" rx="4" fill="#2B579A"/>
          <path d="M14 18h20M14 24h20M14 30h12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          <text x="24" y="38" fill="white" fontSize="9" fontWeight="bold" textAnchor="middle">WORD</text>
        </svg>
      );
    }
    return (
      <svg className="w-10 h-10" viewBox="0 0 48 48" fill="none">
        <rect width="48" height="48" rx="4" fill="#718096"/>
        <path d="M14 18h20M14 24h20M14 30h12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              {onBack && (
                <button
                  onClick={onBack}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Back"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </button>
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">📚 School Policies</h1>
                <p className="text-gray-600 text-sm">Important school policies and documents</p>
              </div>
            </div>
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Home"
              >
                <span className="text-xl">🏠</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading policies...</p>
          </div>
        ) : policies.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Policies Available</h3>
            <p className="text-gray-600">School policies will be displayed here once uploaded</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="divide-y divide-gray-200">
              {policies.map((policy) => (
                <div
                  key={policy.id}
                  className="flex items-center p-4 hover:bg-gray-50 transition-colors"
                >
                  {/* File Icon */}
                  <div className="flex-shrink-0 mr-4">
                    {getFileIcon(policy.file_name)}
                  </div>

                  {/* Policy Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 mb-1">
                      {policy.title}
                    </h3>
                    
                    {policy.description && (
                      <p className="text-sm text-gray-600 mb-2">
                        {policy.description}
                      </p>
                    )}

                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {formatDate(policy.uploaded_at)}
                    </div>
                  </div>

                  {/* View Button */}
                  <div className="flex-shrink-0 ml-4">
                    <a
                      href={buildApiUrl(`/api/school-policies/${policy.id}/download`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentSchoolPolicies;
